import { Box, Button, MenuItem, Select, TextField } from '@mui/material';
import React, { useState } from 'react';

import type { NotificationCategory } from '@/services/api/notificationsService';
import { useNotificationCreateMutation } from '@/services/api/notificationsService';

export const NotificationTesting = () => {
  const [title, setTitle] = useState('Test Notification');
  const [description, setDescription] = useState(
    'This is a test notification from the Zaddy testing page',
  );
  const [category, setCategory] = useState<NotificationCategory | ''>(
    'SUGGESTION',
  );
  const [deepLinkUrl, setDeepLinkUrl] = useState('/matters');
  const [lastResponse, setLastResponse] = useState<string | null>(null);

  const [createNotification, { isLoading: isCreating }] =
    useNotificationCreateMutation();

  const handleCreateNotification = async () => {
    setLastResponse(null);

    try {
      const result = await createNotification({
        createNotificationSchema: {
          user_id: '1',
          title,
          description: description || undefined,
          category: category || undefined,
          deep_link_url: deepLinkUrl || undefined,
        },
      }).unwrap();

      setLastResponse(
        `✅ Notification created successfully!\nID: ${result.id}`,
      );
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'data' in error) {
        setLastResponse(`❌ Error: ${JSON.stringify(error.data, null, 2)}`);
      } else if (error instanceof Error) {
        setLastResponse(`❌ Error: ${error.message}`);
      } else {
        setLastResponse(`❌ Unknown error occurred`);
      }
    }
  };

  const categories: Array<NotificationCategory | ''> = [
    '',
    'SUGGESTION',
    'ASSIGNMENT',
    'ALERT',
    'REMINDER',
    'UPDATE',
    'INFO',
  ];

  return (
    <Box
      sx={{
        p: 3,
        height: '100%',
        overflow: 'auto',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <h2 style={{ marginTop: 0 }}>Notification Testing</h2>
        <p style={{ color: '#666', marginBottom: 32 }}>
          Create test notifications to see them appear in the notification bell
          in the header.
        </p>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            helperText="The main notification title"
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            helperText="Optional description text"
          />

          <Box>
            <Select
              labelId="category-select-label"
              id="category-select"
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as NotificationCategory | '')
              }
              fullWidth
              displayEmpty
            >
              {categories.map((cat) => (
                <MenuItem key={cat || 'none'} value={cat}>
                  {cat || 'None'}
                </MenuItem>
              ))}
            </Select>
            <div
              style={{
                fontSize: 12,
                color: '#666',
                marginTop: 4,
              }}
            >
              Category (optional)
            </div>
          </Box>

          <TextField
            label="Deep Link URL"
            value={deepLinkUrl}
            onChange={(e) => setDeepLinkUrl(e.target.value)}
            fullWidth
            helperText="URL to navigate to when clicking the notification (e.g., /matters, /tasks)"
          />

          <Button
            variant="contained"
            onClick={handleCreateNotification}
            disabled={isCreating || !title}
            sx={{ py: 1.5 }}
          >
            {isCreating ? 'Creating...' : 'Create Notification'}
          </Button>

          {lastResponse && (
            <Box
              sx={{
                p: 2,
                bgcolor: lastResponse.startsWith('✅') ? '#f0fdf4' : '#fef2f2',
                border: 1,
                borderColor: lastResponse.startsWith('✅')
                  ? '#86efac'
                  : '#fca5a5',
                borderRadius: 1,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                fontSize: 13,
              }}
            >
              {lastResponse}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 3,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <h3 style={{ marginTop: 0 }}>Quick Test Scenarios</h3>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setTitle('New task assigned to you');
                setDescription('Review the police report for Matter #12345');
                setCategory('ASSIGNMENT');
                setDeepLinkUrl('/tasks');
              }}
            >
              Assignment Notification
            </Button>

            <Button
              variant="outlined"
              onClick={() => {
                setTitle('AI found a potential suggestion');
                setDescription(
                  'Consider requesting medical records from Dr. Smith',
                );
                setCategory('SUGGESTION');
                setDeepLinkUrl('/matters');
              }}
            >
              Suggestion Notification
            </Button>

            <Button
              variant="outlined"
              onClick={() => {
                setTitle('Deadline approaching');
                setDescription('Document filing due in 2 days');
                setCategory('ALERT');
                setDeepLinkUrl('/tasks');
              }}
            >
              Alert Notification
            </Button>

            <Button
              variant="outlined"
              onClick={() => {
                setTitle('Follow up with client');
                setDescription('Check in about treatment progress');
                setCategory('REMINDER');
                setDeepLinkUrl('/matters');
              }}
            >
              Reminder Notification
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
